import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Paper,
  useTheme,
  alpha,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Call as CallIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  ContentCopy as CopyIcon,
  PersonOutline as PersonOutlineIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ThemeStyles } from '../../theme';
import contactsAPI from '../../api/EnhancedGoogleContactsAPI';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../../firebase';

// Custom ScrollContainer component using theme engine
const ScrollContainer = React.forwardRef(({ style, ...props }, ref) => (
  <Box
    ref={ref}
    {...props}
    component="ul"
    sx={(theme) => ({
      margin: 0,
      padding: '120px 24px 16px 24px', // Increased top padding to account for floating search bar
      listStyle: 'none',
      height: '100%',
      overflowY: 'auto',
      ...ThemeStyles.scrollbar(theme),
      ...style
    })}
  />
));

ScrollContainer.displayName = 'ScrollContainer';

const GoogleContactsPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [showReauthDialog, setShowReauthDialog] = useState(false);

  // Search contacts from Google API
  const searchContacts = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      setError(null);
      
      console.log('Searching for:', query);
      
      // Search both personal and directory contacts (same as contacts.google.com)
      const response = await contactsAPI.searchPersonalContacts(query);
      
      if (response.combined) {
        console.log(`Found ${response.combined.length} matches for "${query}" (${response.contacts?.length || 0} personal + ${response.directory?.length || 0} directory)`);
        setSearchResults(response.combined);
        
        // Show any errors but don't fail completely
        if (response.errors.length > 0) {
          console.warn('Some data sources failed:', response.errors);
        }
      } else {
        setSearchResults([]);
      }
      
    } catch (err) {
      console.error('Error searching contacts:', err);
      if (err.status === 401 || err.status === 403) {
        setHasPermission(false);
        setShowReauthDialog(true);
        setError(null); // Clear error since we're showing dialog
      } else {
        setError('Failed to search contacts. Please try again.');
      }
    } finally {
      setSearching(false);
    }
  }, []);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedContact(null);
  }, []);


  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) { // Only search if at least 2 characters
        searchContacts(searchQuery);
      } else {
        setSearchResults([]); // Clear results for short queries
      }
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, searchContacts]);

  // Copy to clipboard function
  const handleCopy = useCallback((text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({ 
        open: true, 
        message: `${type} copied to clipboard!` 
      });
    }).catch(err => {
      console.error('Failed to copy:', err);
      setSnackbar({ 
        open: true, 
        message: `Failed to copy ${type}` 
      });
    });
  }, []);

  // Call phone number function
  const handleCall = useCallback((phoneNumber) => {
    try {
      // Clean the phone number for tel: protocol
      const cleanNumber = phoneNumber.replace(/[\s\-()]/g, '');
      window.location.href = `tel:${cleanNumber}`;
    } catch (err) {
      console.error('Failed to initiate call:', err);
    }
  }, []);

  // Close contact details function
  const handleCloseContactDetails = useCallback(() => {
    setSelectedContact(null);
  }, []);

  // Handle reauthentication with Google OAuth popup
  const handleReauthentication = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Starting Google Contacts reauthentication...');
      
      // Clear the token cache first
      contactsAPI.clearTokenCache();
      
      // Create Google Auth provider with contacts scope
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/contacts.readonly');
      provider.addScope('https://www.googleapis.com/auth/directory.readonly');
      
      // Open Google OAuth popup
      console.log('🪟 Opening Google OAuth popup...');
      const result = await signInWithPopup(auth, provider);
      
      // Extract the access token
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;
      
      console.log('✅ OAuth popup completed successfully');
      
      if (accessToken) {
        // Store the new token in the enhanced token manager
        const tokenData = {
          accessToken: accessToken,
          refreshToken: credential.refreshToken,
          clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/directory.readonly',
          token_type: 'Bearer'
        };
        
        // Store token with 1 hour expiry (Google default)
        await contactsAPI.tokenManager.storeToken(user.uid, tokenData, 3600);
        
        // Update permissions state
        setHasPermission(true);
        setShowReauthDialog(false);
        
        setSnackbar({
          open: true,
          message: 'Successfully reauthenticated with Google Contacts!',
          severity: 'success'
        });
        
        console.log('✅ Google Contacts reauthentication completed successfully');
      } else {
        throw new Error('No access token received from Google OAuth');
      }
      
    } catch (err) {
      console.error('❌ Reauthentication failed:', err);
      
      // Handle user cancellation gracefully
      if (err.code === 'auth/popup-closed-by-user' || err.message === 'auth_cancelled') {
        setSnackbar({
          open: true,
          message: 'Authentication cancelled by user.',
          severity: 'info'
        });
      } else {
        setError('Reauthentication failed. Please try again.');
        setSnackbar({
          open: true,
          message: 'Reauthentication failed. Please try again.',
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Check if user has required permissions on component mount
  useEffect(() => {
    const checkInitialPermission = async () => {
      if (user?.role === 'admin' || user?.role === 'SOS') {
        try {
          const hasPermission = await contactsAPI.checkPermission();
          setHasPermission(hasPermission);
          
          // If no permission on initial load, show dialog immediately
          if (!hasPermission) {
            setShowReauthDialog(true);
          }
        } catch (err) {
          console.error('Error checking initial permission:', err);
          setHasPermission(false);
          
          // For auth-related errors, show reauthentication dialog
          if (err.status === 401 || err.status === 403 || 
              err.message?.includes('No valid Google OAuth access token') ||
              err.message?.includes('permission denied')) {
            setShowReauthDialog(true);
          }
        }
      }
    };

    if (user) {
      checkInitialPermission();
    }
  }, [user]);

  // Handle authentication events from the API
  useEffect(() => {
    const handleAuthRequired = (event) => {
      console.log('Google Contacts authentication required:', event.detail);
      setHasPermission(false);
      setShowReauthDialog(true);
      setError(null); // Clear error since we're showing dialog
    };

    const handleTokenRefresh = (event) => {
      console.log('Google Contacts token refreshed successfully');
      setHasPermission(true);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Google Contacts access refreshed successfully.',
        severity: 'success'
      });
    };

    // Listen for authentication events
    window.addEventListener('contacts-auth-required', handleAuthRequired);
    window.addEventListener('contacts-token-refreshed', handleTokenRefresh);

    return () => {
      window.removeEventListener('contacts-auth-required', handleAuthRequired);
      window.removeEventListener('contacts-token-refreshed', handleTokenRefresh);
    };
  }, []);

  if (!user || (user.role !== 'admin' && user.role !== 'SOS')) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Access Denied. This feature is only available to Admin and SOS users.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      pt: { xs: '20px', sm: '30px' } // Additional spacing from header
    }}>
      <Container maxWidth="xl" sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 0, sm: 0 },
        overflow: 'hidden'
      }}>
        {/* Header Section - Above main card */}
        <Box sx={{ 
          mb: 4, 
          px: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/app')} 
              sx={{
                color: 'text.secondary',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
                },
                borderRadius: 2
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography 
                  variant="h4" 
                  component="h1"
                  sx={{ 
                    fontFamily: 'StrawberrySansDisplay',
                    fontWeight: 400,
                    color: 'text.primary',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    letterSpacing: '-0.02em'
                  }}
                >
                  Strawberry Contacts
                </Typography>
                {hasPermission && (
                  <Chip
                    icon={
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          ml: 0.5
                        }}
                      />
                    }
                    label="Connected"
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.7rem',
                      bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                      color: 'success.main',
                      border: '1px solid',
                      borderColor: theme => alpha(theme.palette.success.main, 0.2),
                      fontWeight: 500
                    }}
                  />
                )}
              </Box>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  opacity: 0.8,
                  fontFamily: 'StrawberrySansDisplay',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Search and access your personal Google Contacts and organization directory with Strawberry's enhanced interface.
              </Typography>
            </Box>
          </Box>
          

        </Box>

        {/* Error Alert Only */}
        {error && (
          <Box sx={{ mb: 3, px: { xs: 2, sm: 3 } }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Main content container with app.js styling */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          bgcolor: 'background.paper',
          border: 'none',
          borderRadius: { xs: 0, sm: '28px' },
          flex: 1,
          mt: { xs: 0, sm: 0 },
          mb: { xs: 0, sm: 2 },
          mx: { xs: 0, sm: 2 },
          boxShadow: { 
            xs: 'none', 
            sm: theme => `0 4px 24px ${alpha(theme.palette.common.black, 0.03)}` 
          },
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          position: 'relative',
          '&:hover': {
            boxShadow: { 
              xs: 'none', 
              sm: theme => `
                0 8px 32px ${alpha(theme.palette.common.black, 0.06)},
                0 1px 1px ${alpha(theme.palette.primary.main, 0.04)}
              `
            },
          }
        }}>
          {/* Search Results Panel */}
          <Box sx={{ 
            width: { xs: '100%', md: selectedContact ? '60%' : '100%' },
            height: { xs: (selectedContact ? '0px' : '100%'), md: '100%' },
            maxHeight: { xs: '100%', md: '100%' },
            borderRight: selectedContact ? '1px solid' : 'none',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            visibility: { xs: (selectedContact ? 'hidden' : 'visible'), md: 'visible' },
            opacity: { xs: (selectedContact ? 0 : 1), md: 1 },
            transition: 'all 0.3s ease',
          }}>
            {/* Search Results Content */}
            <ScrollContainer>

              {/* Search Results */}
              {hasPermission && (
                <>
                  {/* Search results count message */}
                  {searchQuery && searchResults.length > 0 && (
                    <Box 
                      sx={{ 
                        mb: 2, 
                        p: 2, 
                        borderRadius: 2,
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
                        border: '1px solid',
                        borderColor: theme => alpha(theme.palette.primary.main, 0.15),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'primary.main'
                          }}
                        >
                          <SearchIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.primary',
                              fontWeight: 600,
                              fontSize: '0.875rem'
                            }}
                          >
                            {searchResults.length} {searchResults.length === 1 ? 'contact' : 'contacts'} found
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary',
                              fontSize: '0.75rem'
                            }}
                          >
                            Search: "{searchQuery}"
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        onClick={handleClearSearch}
                        startIcon={<ClearIcon />}
                        variant="outlined"
                        sx={{ 
                          borderRadius: 1.5,
                          height: 32,
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          borderColor: theme => alpha(theme.palette.divider, 0.2),
                          '&:hover': {
                            color: 'error.main',
                            borderColor: theme => alpha(theme.palette.error.main, 0.3),
                            bgcolor: theme => alpha(theme.palette.error.main, 0.05)
                          }
                        }}
                      >
                        Clear
                      </Button>
                    </Box>
                  )}

                  {/* Results or Empty State */}
                  {searching ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : searchResults.length === 0 ? (
                    <Box>
                      {searchQuery ? (
                        // No results for search query
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                            No contacts found
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Try different search terms or check your spelling
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            sx={{ borderRadius: 2 }}
                          >
                            Clear search
                          </Button>
                        </Box>
                      ) : (
                        // Empty state with helpful information
                        <Box sx={{ py: 6 }}>
                          <Box sx={{ textAlign: 'center', mb: 5 }}>
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3
                              }}
                            >
                              <PersonOutlineIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                            </Box>
                            <Typography 
                              variant="h5" 
                              sx={{ 
                                mb: 1.5,
                                fontWeight: 500,
                                fontFamily: 'StrawberrySansDisplay',
                                color: 'text.primary'
                              }}
                            >
                              Strawberry Contacts
                            </Typography>
                            <Typography 
                              variant="body1" 
                              color="text.secondary"
                              sx={{ 
                                maxWidth: 500,
                                mx: 'auto',
                                mb: 4,
                                lineHeight: 1.6
                              }}
                            >
                              Search your personal Google Contacts and organization directory. 
                              Find people by name, email, or phone number.
                            </Typography>
                          </Box>

                          {/* Helpful tips grid */}
                          <Box 
                            sx={{ 
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                              gap: 2.5,
                              maxWidth: 900,
                              mx: 'auto',
                              px: { xs: 2, sm: 0 }
                            }}
                          >
                            {/* Search tip */}
                            <Paper
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: theme => alpha(theme.palette.background.default, 0.6),
                                border: '1px solid',
                                borderColor: theme => alpha(theme.palette.divider, 0.1),
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                textAlign: 'left',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                                  bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1,
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 2,
                                  color: 'primary.main'
                                }}
                              >
                                <SearchIcon sx={{ fontSize: 20 }} />
                              </Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Quick Search
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                                Type at least 2 characters to start searching across all your contacts instantly.
                              </Typography>
                            </Paper>

                            {/* Personal contacts tip */}
                            <Paper
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: theme => alpha(theme.palette.background.default, 0.6),
                                border: '1px solid',
                                borderColor: theme => alpha(theme.palette.divider, 0.1),
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                textAlign: 'left',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                                  bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1,
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 2,
                                  color: 'primary.main'
                                }}
                              >
                                <EmailIcon sx={{ fontSize: 20 }} />
                              </Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Personal Contacts
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                                Access all contacts from your Google account, including names, emails, and phone numbers.
                              </Typography>
                            </Paper>

                            {/* Directory tip */}
                            <Paper
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: theme => alpha(theme.palette.background.default, 0.6),
                                border: '1px solid',
                                borderColor: theme => alpha(theme.palette.divider, 0.1),
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                textAlign: 'left',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                                  bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1,
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 2,
                                  color: 'primary.main'
                                }}
                              >
                                <WorkIcon sx={{ fontSize: 20 }} />
                              </Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Organization Directory
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                                Search your organization's directory to find colleagues and team members across departments.
                              </Typography>
                            </Paper>
                          </Box>

                          {/* Search examples */}
                          <Box sx={{ mt: 5, textAlign: 'center' }}>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                mb: 2,
                                fontSize: '0.875rem'
                              }}
                            >
                              Try searching for:
                            </Typography>
                            <Box 
                              sx={{ 
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                justifyContent: 'center',
                                maxWidth: 600,
                                mx: 'auto'
                              }}
                            >
                              {['First name', 'Last name', 'Email address', 'Phone number', 'Organization'].map((example, index) => (
                                <Chip
                                  key={index}
                                  label={example}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: theme => alpha(theme.palette.divider, 0.2),
                                    color: 'text.secondary',
                                    bgcolor: theme => alpha(theme.palette.background.default, 0.5),
                                    fontSize: '0.75rem',
                                    height: 28,
                                    '&:hover': {
                                      borderColor: theme => alpha(theme.palette.primary.main, 0.3),
                                      bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {searchResults.map((contact, index) => (
                        <Paper
                          key={contact.id}
                          onClick={() => setSelectedContact(contact)}
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2.5,
                            transition: 'all 0.2s ease',
                            bgcolor: selectedContact?.id === contact.id 
                              ? alpha(theme.palette.primary.main, 0.08)
                              : 'background.default',
                            border: '1px solid',
                            borderColor: selectedContact?.id === contact.id
                              ? alpha(theme.palette.primary.main, 0.2)
                              : alpha(theme.palette.divider, 0.08),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              borderColor: alpha(theme.palette.primary.main, 0.15),
                              transform: 'translateY(-1px)',
                              boxShadow: theme => `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
                            },
                            ...(selectedContact?.id === contact.id && {
                              boxShadow: theme => `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`,
                            })
                          }}
                        >
                          <Avatar 
                            src={contact.photo}
                            sx={{ 
                              bgcolor: 'primary.main',
                              width: 52,
                              height: 52,
                              fontSize: '1.1rem',
                              fontWeight: 500
                            }}
                          >
                            {contact.photo ? 
                              contact.name?.charAt(0)?.toUpperCase() || '?' : 
                              <PersonOutlineIcon sx={{ fontSize: 28 }} />
                            }
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: '0.95rem',
                                  color: 'text.primary'
                                }}
                              >
                                {contact.name || 'Unnamed Contact'}
                              </Typography>
                              {contact.isDirectory && (
                                <Chip 
                                  label="Directory" 
                                  size="small" 
                                  sx={{ 
                                    height: 22, 
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                    color: 'primary.main',
                                    border: '1px solid',
                                    borderColor: theme => alpha(theme.palette.primary.main, 0.2)
                                  }} 
                                />
                              )}
                            </Box>
                            {contact.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ 
                                    fontSize: '0.85rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {contact.email}
                                </Typography>
                              </Box>
                            )}
                            {contact.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ fontSize: '0.85rem' }}
                                >
                                  {contact.phone}
                                </Typography>
                              </Box>
                            )}
                            {contact.organization && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <WorkIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ 
                                    fontSize: '0.85rem',
                                    fontStyle: 'italic'
                                  }}
                                >
                                  {contact.organization}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </ScrollContainer>

            {/* Floating Search Area with Frosted Glass Effect */}
            {hasPermission && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10,
                pt: { xs: 2, sm: 3 },
                px: { xs: 2, sm: 3 },
                pb: 2,
                // Frosted glass effect
                bgcolor: theme => alpha(theme.palette.background.paper, 0.85),
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)', // Safari support
                borderBottom: '1px solid',
                borderColor: theme => alpha(theme.palette.divider, 0.2),
                // Subtle shadow to enhance the floating effect
                boxShadow: theme => `0 4px 32px ${alpha(theme.palette.common.black, 0.08)}`,
              }}>
                <TextField
                  fullWidth
                  placeholder="Search contacts and directory by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '52px',
                      bgcolor: theme => alpha(theme.palette.background.default, 0.7),
                      borderRadius: '32px',
                      border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: theme => `0 2px 16px ${alpha(theme.palette.common.black, 0.04)}`,
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                        bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                      },
                      '&.Mui-focused': {
                        boxShadow: theme => `
                          0 4px 20px ${alpha(theme.palette.common.black, 0.12)},
                          0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}
                        `,
                        bgcolor: theme => alpha(theme.palette.background.default, 0.9),
                      }
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Contact Details Panel */}
          {selectedContact && (
            <Box sx={{
              width: { xs: '100%', md: '40%' },
              height: { xs: '100%', md: '100%' },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <Box sx={{ 
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Fixed Header */}
                <Box sx={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  pt: 3,
                  pb: 2.5,
                  px: 3,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: theme => alpha(theme.palette.divider, 0.1),
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      textAlign: 'center',
                      fontFamily: 'StrawberrySansDisplay',
                      fontSize: '1.1rem'
                    }}
                  >
                    Contact Details
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={handleCloseContactDetails}
                    sx={{ 
                      position: 'absolute',
                      right: 16,
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'text.primary',
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                {/* Scrollable Content */}
                <ScrollContainer sx={{ 
                  padding: '100px 24px 24px 24px', // Extra top padding for fixed header
                }}>
                
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Avatar
                      src={selectedContact.photo}
                      sx={{
                        width: 100,
                        height: 100,
                        bgcolor: 'primary.main',
                        fontSize: '2.5rem',
                        fontWeight: 500,
                        mx: 'auto',
                        mb: 2.5,
                        boxShadow: theme => `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
                      }}
                    >
                      {selectedContact.name?.charAt(0)?.toUpperCase() || '?'}
                    </Avatar>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 600,
                        mb: 0.5,
                        fontFamily: 'StrawberrySansDisplay'
                      }}
                    >
                      {selectedContact.name || 'Unnamed Contact'}
                    </Typography>
                    {selectedContact.isDirectory && (
                      <Chip 
                        label="Organization Directory" 
                        size="small" 
                        sx={{ 
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          border: '1px solid',
                          borderColor: theme => alpha(theme.palette.primary.main, 0.2)
                        }} 
                      />
                    )}
                  </Box>

                  <Box sx={{ space: 2 }}>
                    {/* Display all email addresses */}
                    {selectedContact.rawContact?.emailAddresses?.map((email, index) => (
                      <Paper 
                        key={`email-${index}`} 
                        elevation={0}
                        sx={{ 
                          p: 2.5, 
                          mb: 2, 
                          bgcolor: theme => alpha(theme.palette.background.default, 0.6),
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: theme => alpha(theme.palette.divider, 0.12),
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                            boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'primary.main',
                                flexShrink: 0
                              }}
                            >
                              <EmailIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  mb: 0.5,
                                  display: 'block'
                                }}
                              >
                                {email.type || 'Email'} {email.formattedType && `(${email.formattedType})`}
                              </Typography>
                              <Typography 
                                variant="body2"
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {email.value}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton 
                            size="small"
                            onClick={() => handleCopy(email.value, 'Email')}
                            sx={{ 
                              ml: 1,
                              flexShrink: 0,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main'
                              }
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}

                    {/* Display all phone numbers */}
                    {selectedContact.rawContact?.phoneNumbers?.map((phone, index) => (
                      <Paper 
                        key={`phone-${index}`}
                        elevation={0}
                        sx={{ 
                          p: 2.5, 
                          mb: 2, 
                          bgcolor: theme => alpha(theme.palette.background.default, 0.6),
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: theme => alpha(theme.palette.divider, 0.12),
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                            boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: theme => alpha(theme.palette.success.main, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'success.main',
                                flexShrink: 0
                              }}
                            >
                              <PhoneIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  mb: 0.5,
                                  display: 'block'
                                }}
                              >
                                {phone.type || 'Phone'} {phone.formattedType && `(${phone.formattedType})`}
                              </Typography>
                              <Typography 
                                variant="body2"
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.9rem'
                                }}
                              >
                                {phone.value}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                            <IconButton 
                              size="small"
                              onClick={() => handleCall(phone.value)}
                              sx={{ 
                                color: 'success.main',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                                }
                              }}
                            >
                              <CallIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small"
                              onClick={() => handleCopy(phone.value, 'Phone')}
                              sx={{ 
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main'
                                }
                              }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    ))}

                    {/* Display organization information */}
                    {selectedContact.rawContact?.organizations?.map((org, index) => (
                      <Paper 
                        key={`org-${index}`}
                        elevation={0}
                        sx={{ 
                          p: 2.5, 
                          mb: 2, 
                          bgcolor: theme => alpha(theme.palette.background.default, 0.6),
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: theme => alpha(theme.palette.divider, 0.12),
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            bgcolor: theme => alpha(theme.palette.background.default, 0.8),
                            boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'warning.main',
                              flexShrink: 0
                            }}
                          >
                            <WorkIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ 
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                mb: 1,
                                display: 'block'
                              }}
                            >
                              {org.type || 'Organization'}
                            </Typography>
                            {org.name && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: '0.95rem',
                                  mb: 0.5
                                }}
                              >
                                {org.name}
                              </Typography>
                            )}
                            {org.title && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: '0.875rem',
                                  mb: org.department || org.jobDescription ? 0.5 : 0
                                }}
                              >
                                {org.title}
                              </Typography>
                            )}
                            {org.department && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  fontSize: '0.875rem',
                                  mb: org.jobDescription ? 0.5 : 0
                                }}
                              >
                                {org.department}
                              </Typography>
                            )}
                            {org.jobDescription && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  fontSize: '0.875rem',
                                  fontStyle: 'italic',
                                  mt: 0.5
                                }}
                              >
                                {org.jobDescription}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
                </ScrollContainer>
              </Box>
            </Box>
          )}
        </Box>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'warning' ? 6000 : 3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root, & .MuiAlert-root': {
            '&::after': { display: 'none' },
          },
        }}
      >
        <Alert 
          onClose={() => setSnackbar({ open: false, message: '' })}
          severity={snackbar.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Reauthentication Dialog - Matching Handbook Library Design */}
      <Dialog 
        open={showReauthDialog} 
        onClose={() => setShowReauthDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            maxWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          pb: 1
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'common.white'
          }}>
            <LockIcon />
          </Box>
          Google Contacts Access Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            To access your contacts, you need to connect your Google account.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We only request read-only access to view your contacts. Your data remains secure.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setShowReauthDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReauthentication}
            variant="contained"
            sx={{ borderRadius: 2 }}
            disabled={loading}
            autoFocus
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Connecting...' : 'Connect Google Contacts'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoogleContactsPage;